import React from 'react';
import { FiTool } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const WorkRateLesson = () => {
  const sections = [
    {
      id: 'individual-work',
      title: 'Individual Work Problems',
      content: {
        introduction:
          'Work problems involve rate, time, and amount of work completed. The fundamental principle is: Work = Rate × Time. Understanding individual rates is the foundation for solving complex work problems.',

        concepts: [
          {
            title: 'Work Formula',
            explanation:
              'The fundamental principle is §Work = Rate \\times Time§. Understanding individual rates is the foundation for solving complex work problems.',
            example: {
              problem:
                'John can paint a room in 6 hours. What portion of the room can he paint in 1 hour?',
              solution: [
                "John's rate = §\\frac{1 \\text{ room}}{6 \\text{ hours}}§",
                'Work = §Rate \\times Time§ = §(\\frac{1}{6} \\frac{\\text{room}}{\\text{hour}}) \\times (1 \\text{ hour})§',
                'Work = §\\frac{1}{6}§ of the room',
                'In 1 hour, John paints §\\frac{1}{6}§ of the room.',
              ],
            },
          },
          {
            title: 'Finding Time Given Rate',
            explanation:
              'If you know the rate and amount of work, you can find the total time using §Time = \\frac{Work}{Rate}§.',
            example: {
              problem:
                'Sarah can type a document in 70 hours. How long will it take her to get through an entire document?',
              solution: [
                "Sarah's Rate = §\\frac{1}{70}§ document per hour",
                'Work = 1 document',
                'Time = §\\frac{Work}{Rate} = \\frac{1}{\\frac{1}{70}}§',
                'Time = §1 \\times 70 = 70§ hours',
                'It will take Sarah 70 hours to type the document.',
              ],
            },
          },
          {
            title: 'Fractional Work Completed',
            explanation:
              'To find what fraction of work is done: §Fraction = \\frac{\\text{Time Worked}}{\\text{Total Time to Complete}}§.',
            example: {
              problem:
                'Mike can complete a project in 8 hours. If he works for 3 hours, what fraction of the project is completed?',
              solution: [
                'Time worked = 3 hours',
                'Total time = 8 hours',
                'Work completed = §\\frac{3}{8}§ of the project',
                'Remaining work = §1 - \\frac{3}{8} = \\frac{5}{8}§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'combined-work',
      title: 'Combined Work Problems',
      content: {
        introduction:
          'When multiple people or machines work together, their rates add. The combined rate equals the sum of individual rates. This is one of the most common GRE work problem types.',

        concepts: [
          {
            title: 'Adding Rates',
            explanation:
              'When two or more workers collaborate: §\\text{Combined Rate} = \\text{Rate}_A + \\text{Rate}_B§. Then use §Time = \\frac{Work}{Combined Rate}§.',
            greTrap:
              'Do not add the times. If one worker takes 4 hours and another takes 6 hours, add §1/4 + 1/6§, not §4 + 6§.',
            example: {
              problem:
                'Alice can paint a fence in 4 hours. Bob can paint it in 6 hours. How long will it take if they work together?',
              solution: [
                "Alice's rate = §\\frac{1}{4}§ fence/hr",
                "Bob's rate = §\\frac{1}{6}§ fence/hr",
                'Combined rate = §\\frac{1}{4} + \\frac{1}{6} = \\frac{3}{12} + \\frac{2}{12} = \\frac{5}{12}§ fence/hr',
                'Time = §\\frac{1}{\\frac{5}{12}} = \\frac{12}{5} = 2.4§ hours',
              ],
            },
          },
          {
            title: 'Three or More Workers',
            explanation:
              'The same principle applies with any number of workers. Add all individual rates, then divide work by the combined rate.',
            example: {
              problem:
                'Machine A completes a job in 10 hours, Machine B in 15 hours, and Machine C in 20 hours. How long to complete the job together?',
              solution: [
                'Rate A = §\\frac{1}{10}§, Rate B = §\\frac{1}{15}§, Rate C = §\\frac{1}{20}§',
                'Combined rate = §\\frac{1}{10} + \\frac{1}{15} + \\frac{1}{20} = \\frac{6+4+3}{60} = \\frac{13}{60}§',
                'Time = §\\frac{1}{\\frac{13}{60}} = \\frac{60}{13} \\approx 4.62§ hours',
                'So, about 4 hours 37 minutes',
              ],
            },
          },
          {
            title: 'Part-Time Collaboration',
            explanation:
              'Sometimes workers collaborate for part of the time. Calculate work done together, then add remaining individual work.',
            greTrap:
              'Separate the together phase from the solo phase. Do not apply the combined rate to the entire job time unless they work together the entire time.',
            example: {
              problem:
                'Tom can do a job in 6 hours. Jerry can do it in 8 hours. They work together for 2 hours, then Tom finishes alone. How long total?',
              solution: [
                'Tom rate = §\\frac{1}{6}§, Jerry rate = §\\frac{1}{8}§',
                'Combined rate = §\\frac{1}{6} + \\frac{1}{8} = \\frac{4+3}{24} = \\frac{7}{24}§',
                'Work done together = §\\frac{7}{24} \\times 2 = \\frac{14}{24} = \\frac{7}{12}§',
                'Work remaining = §1 - \\frac{7}{12} = \\frac{5}{12}§',
                'Time for Tom alone = §\\frac{\\frac{5}{12}}{\\frac{1}{6}} = \\frac{5}{12} \\times 6 = \\frac{5}{2} = 2.5§ hours',
                'Total time = §2 + 2.5 = 4.5§ hours',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'rate-differences',
      title: 'Rate Differences & Efficiency',
      content: {
        introduction:
          'Some problems involve comparing rates or dealing with varying efficiency. Understanding how rates differ helps solve comparison and optimization problems.',

        concepts: [
          {
            title: 'Rate Comparison',
            explanation:
              'To compare how much faster one worker is than another, find the ratio of their rates or times.',
            example: {
              problem:
                'Machine X produces 20 units per hour. Machine Y produces 60 units per hour. How much more efficient is Machine Y?',
              solution: [
                'Rate X = §20§ units/hr',
                'Rate Y = §60§ units/hr',
                'Ratio = §\\frac{Rate_Y}{Rate_X} = \\frac{60}{20} = 3§',
                'Machine Y is 3 times as efficient as Machine X.',
                'Or, as a percent: §\\frac{60 - 20}{20} \\times 100\\% = \\frac{40}{20} \\times 100\\% = 200\\%§ more efficient than Machine X.',
              ],
            },
          },
          {
            title: 'Opposite Work (Filling and Emptying)',
            explanation:
              'When one process fills and another empties, subtract the rates: §Net Rate = Rate_{fill} - Rate_{empty}§.',
            example: {
              problem:
                'A pipe fills a tank in 4 hours. A drain empties it in 6 hours. If both are open, how long to fill the tank?',
              solution: [
                'Fill rate = §\\frac{1}{4}§ tank per hour (positive)',
                'Drain rate = §\\frac{1}{6}§ tank per hour (negative)',
                'Net rate = §\\frac{1}{4} - \\frac{1}{6} = \\frac{3-2}{12} = \\frac{1}{12}§',
                'Time to fill = §\\frac{1}{\\frac{1}{12}} = 12§ hours',
              ],
            },
          },
          {
            title: 'Variable Efficiency',
            explanation:
              'Sometimes efficiency changes over time. Break the problem into stages and calculate work for each stage.',
            example: {
              problem:
                'An intern must process 60 forms. He works at a rate of §10§ forms/hr for 3 hours. He then gets tired and does §5§ forms/hr for 2 hours. What fraction of the job is done?',
              solution: [
                'Work in first 3 hours = §10§ forms/hr §\\times 3 \\text{ hr} = 30§ forms',
                'Work in next 2 hours = §5§ forms/hr §\\times 2 \\text{ hr} = 10§ forms',
                'Total work done = §30 + 10 = 40§ forms',
                'Fraction completed = §\\frac{40}{60} = \\frac{2}{3}§ of the job is done.',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'complex-scenarios',
      title: 'Complex Work Scenarios',
      content: {
        introduction:
          'Advanced work problems may involve breaks, changing crews, or multiple jobs. The key is to break complex problems into manageable parts using the fundamental W = r × t formula.',

        concepts: [
          {
            title: 'Work with Breaks',
            explanation:
              'When workers take breaks, only count active working time. §\\text{Total Time} = \\text{Work Time} + \\text{Break Time}§.',
            example: {
              problem:
                'Maria can complete a task in 5 hours. She works for 2 hours, takes a 30-minute break, works for 2 more hours. How much time has elapsed and what fraction is done?',
              solution: [
                "Maria's rate = §\\frac{1}{5}§ task/hr",
                'Work done = §\\frac{1}{5} \\times (2+2) = \\frac{4}{5}§ of the task',
                'Total time elapsed = 2 hours work + 0.5 hours break + 2 hours work = 4.5 hours',
                'Remaining work = §1 - \\frac{4}{5} = \\frac{1}{5}§ of the task',
              ],
            },
          },
          {
            title: 'Sequential Workers',
            explanation:
              'When workers start at different times or work in sequence, calculate the work each completes during their shift.',
            example: {
              problem:
                'A can complete a job in 6 hours. B can complete it in 4 hours. A works alone for 2 hours, then B finishes. How long does it work?',
              solution: [
                'Rate A = §\\frac{1}{6}§, Rate B = §\\frac{1}{4}§',
                'Work by A = §\\frac{1}{6} \\times 2 = \\frac{1}{3}§',
                'Work remaining = §1 - \\frac{1}{3} = \\frac{2}{3}§',
                'Time for B = §\\frac{\\text{Work}}{\\text{Rate}} = \\frac{\\frac{2}{3}}{\\frac{1}{4}} = \\frac{2}{3} \\times 4 = \\frac{8}{3} \\approx 2.67§ hours',
                'B works for §\\approx 2.67§ hours',
              ],
            },
          },
          {
            title: 'Proportional Distribution',
            explanation:
              'When splitting work based on rates, each partner does work proportional to their rate.',
            example: {
              problem:
                'Three workers A, B, C have rates in the ratio §1:2:3§. They work together for 3 hours. What fraction did A complete?',
              solution: [
                'Total rate parts = §1+2+3=6§',
                "A's rate is §\\frac{1}{6}§ of the total rate.",
                "A's contribution to work is also §\\frac{1}{6}§ of the total.",
                'A completes §\\frac{1}{6}§ of the job.',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'Sarah can complete a project in 8 hours. What fraction of the project can she complete in 3 hours?',
      options: ['1/4', '3/8', '1/3', '2/5', '1/2'],
      correct: 1,
      explanation:
        "Sarah's rate = §1/8§ per hour. In 3 hours: §3 × (1/8) = 3/8§ of the project.",
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'Machine A can produce 600 units in 10 hours. Machine B can produce 600 units in 15 hours. Working together, how long will it take to produce 600 units?',
      options: ['5 hours', '6 hours', '7 hours', '8 hours', '9 hours'],
      correct: 1,
      explanation:
        'Rate A = §1/10§, Rate B = §1/15§. Combined = §1/10 + 1/15 = 3/30 + 2/30 = 5/30 = 1/6§. Time = §1/(1/6) = 6§ hours.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'A pipe can fill a tank in 12 hours. Another pipe can empty the same tank in 18 hours. If both pipes are open, how long will it take to fill the tank?',
      options: ['24 hours', '30 hours', '36 hours', '42 hours', '48 hours'],
      correct: 2,
      explanation:
        'Fill rate = §1/12§, Drain rate = §1/18§. Net rate = §1/12 - 1/18 = 3/36 - 2/36 = 1/36§. Time = §36§ hours.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'John can build a wall in 6 days. Mike can build the same wall in 8 days. They work together for 3 days. What fraction of the wall is completed?',
      options: ['5/8', '3/4', '7/8', '11/12', '23/24'],
      correct: 2,
      explanation:
        'Rate John = §1/6§, Rate Mike = §1/8§. Combined = §1/6 + 1/8 = 4/24 + 3/24 = 7/24§. In 3 days: §3 × 7/24 = 21/24 = 7/8§.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'Three printers can print a batch of documents in 4, 6, and 12 hours respectively. How long will it take all three working together?',
      options: ['1.5 hours', '2 hours', '2.5 hours', '3 hours', '3.5 hours'],
      correct: 1,
      explanation:
        'Rates: §1/4 + 1/6 + 1/12§. LCD=12: §3/12 + 2/12 + 1/12 = 6/12 = 1/2§. Time = §1/(1/2) = 2§ hours.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'Machine X produces 100 units in 5 hours. Machine Y produces 100 units in 4 hours. Working together, how many units do they produce in 2 hours?',
      options: ['70', '90', '110', '120', '140'],
      correct: 1,
      explanation:
        'Rate X = §100/5 = 20§ units/hour. Rate Y = §100/4 = 25§ units/hour. Combined = §45§ units/hour. In 2 hours: §45 × 2 = 90§ units.',
    },
    {
      type: 'timed',
      timeLimit: 140,
      question:
        'A and B can paint a fence together in 6 hours. A alone takes 10 hours. How long would B alone take?',
      options: ['15 hours', '12 hours', '18 hours', '20 hours', '16 hours'],
      correct: 0,
      explanation:
        'Rate A = §1/10§, Rate together = §1/6§. Rate B = §1/6 - 1/10§. Common denominator 30: §5/30 - 3/30 = 2/30 = 1/15§. B takes §15§ hours.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'If 5 workers can complete a job in 12 days, how long will it take 8 workers to complete the same job (assuming same rate per worker)?',
      options: ['6 days', '7.5 days', '8 days', '9 days', '10 days'],
      correct: 1,
      explanation:
        'Total work = §5 × 12 = 60§ worker-days. With 8 workers: §60/8 = 7.5§ days.',
    },
    {
      type: 'timed',
      timeLimit: 140,
      question:
        'Anna can type a document in 4 hours. Ben can type the same document in 6 hours. They work together for 1 hour, then Anna finishes alone. What is the total time?',
      options: ['2.7 hours', '2.8 hours', '3 hours', '3.2 hours', '3.3 hours'],
      correct: 4,
      explanation:
        "Anna's rate = §1/4§, Ben's rate = §1/6§. Combined rate = §1/4 + 1/6 = 3/12 + 2/12 = 5/12§. Work done together in 1 hour = §5/12§. Remaining work = §1 - 5/12 = 7/12§. Time for Anna alone = §(7/12) ÷ (1/4) = (7/12) × 4 = 7/3 ≈ 2.33§ hours. Total time = §1 + 2.33 = 3.33§ hours.",
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'A factory has §3§ machines. Machine X produces §20§ units/hour, Machine Y produces 30 units/hour, and Machine Z produces 40 units/hour. How many hours to produce 540 units with all machines?',
      options: ['4 hours', '5 hours', '6 hours', '7 hours', '8 hours'],
      correct: 2,
      explanation:
        'Combined rate = §20 + 30 + 40 = 90§ units/hour. Time = §540/90 = 6§ hours.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'Tom can paint a house in 10 days. Jerry can paint it in 15 days. Tom works alone for 4 days, then they work together. How many more days to finish?',
      options: ['2 days', '3 days', '3.6 days', '4 days', '4.8 days'],
      correct: 2,
      explanation:
        'Tom alone for 4 days: §4 × 1/10 = 4/10 = 2/5§ done. Remaining = §3/5§. Combined rate = §1/10 + 1/15 = 3/30 + 2/30 = 5/30 = 1/6§. Time together = §(3/5) / (1/6) = 3/5 × 6 = 18/5 = 3.6§ days.',
    },
    {
      type: 'timed',
      timeLimit: 140,
      question:
        'A swimming pool can be filled by Pipe A in 8 hours and by Pipe B in 12 hours. A drain can empty it in 24 hours. With all three open, how long to fill the pool?',
      options: ['4 hours', '4.8 hours', '5.5 hours', '6 hours', '6.5 hours'],
      correct: 3,
      explanation:
        'Rate A = §1/8§, Rate B = §1/12§, Drain = §-1/24§. Net rate = §1/8 + 1/12 - 1/24§. Convert to common denominator 24: §3/24 + 2/24 - 1/24 = 4/24 = 1/6§. Time = §1 ÷ (1/6) = 6§ hours.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'If 8 workers can complete a task in 12 days, how many workers are needed to complete it in 6 days?',
      options: ['16', '12', '20', '14', '10'],
      correct: 0,
      explanation:
        'Total work = §8 × 12 = 96§ worker-days. For 6 days: Workers = §96/6 = 16§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A printer can print 600 pages in 10 minutes. At this rate, how long to print 1500 pages?',
      options: [
        '25 minutes',
        '20 minutes',
        '30 minutes',
        '15 minutes',
        '35 minutes',
      ],
      correct: 0,
      explanation:
        'Rate = §600/10 = 60§ pages/minute. Time = §1500/60 = 25§ minutes.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'John can mow a lawn in 40 minutes, and Sarah can mow the same lawn in 60 minutes. If they work together for 15 minutes, what fraction of the lawn is mowed?',
      options: ['5/8', '1/2', '3/4', '2/3', '7/12'],
      correct: 0,
      explanation:
        'Rate John = §1/40§, Rate Sarah = §1/60§. Combined = §1/40 + 1/60 = 3/120 + 2/120 = 5/120 = 1/24§. In 15 min: §15 × (1/24) = 15/24 = 5/8§.',
    },

    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'Sarah can complete a project in 8 hours. What fraction of the project can she complete in 3 hours?',
      options: ['1/4', '3/8', '1/3', '2/5', '1/2'],
      correct: 1,
      explanation:
        "Sarah's rate = §1/8§ per hour. In 3 hours: §3 × (1/8) = 3/8§ of the project.",
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'Machine A can produce 600 units in 10 hours. Machine B can produce 600 units in 15 hours. Working together, how long will it take to produce 600 units?',
      options: ['5 hours', '6 hours', '7 hours', '8 hours', '9 hours'],
      correct: 1,
      explanation:
        'Rate A = §1/10§, Rate B = §1/15§. Combined = §1/10 + 1/15 = 3/30 + 2/30 = 5/30 = 1/6§. Time = §1/(1/6) = 6§ hours.',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question:
        'A pipe can fill a tank in 12 hours. Another pipe can empty the same tank in 18 hours. If both pipes are open, how long will it take to fill the tank?',
      options: ['24 hours', '30 hours', '36 hours', '42 hours', '48 hours'],
      correct: 2,
      explanation:
        'Fill rate = §1/12§, Drain rate = §1/18§. Net rate = §1/12 - 1/18 = 3/36 - 2/36 = 1/36§. Time = §36§ hours.',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'John can build a wall in 6 days. Mike can build the same wall in 8 days. They work together for 3 days. What fraction of the wall is completed?',
      options: ['5/8', '3/4', '7/8', '11/12', '23/24'],
      correct: 2,
      explanation:
        'Rate John = §1/6§, Rate Mike = §1/8§. Combined = §1/6 + 1/8 = 4/24 + 3/24 = 7/24§. In 3 days: §3 × 7/24 = 21/24 = 7/8§.',
    },
    {
      type: 'untimed',
      timeLimit: 130,
      question:
        'Three printers can print a batch of documents in 4, 6, and 12 hours respectively. How long will it take all three working together?',
      options: ['1.5 hours', '2 hours', '2.5 hours', '3 hours', '3.5 hours'],
      correct: 1,
      explanation:
        'Rates: §1/4 + 1/6 + 1/12§. LCD=12: §3/12 + 2/12 + 1/12 = 6/12 = 1/2§. Time = §1/(1/2) = 2§ hours.',
    },
    {
      type: 'untimed',
      timeLimit: 130,
      question:
        'Machine X produces 100 units in 5 hours. Machine Y produces 100 units in 4 hours. Working together, how many units do they produce in 2 hours?',
      options: ['70', '90', '110', '120', '140'],
      correct: 1,
      explanation:
        'Rate X = §100/5 = 20§ units/hour. Rate Y = §100/4 = 25§ units/hour. Combined = §45§ units/hour. In 2 hours: §45 × 2 = 90§ units.',
    },
    {
      type: 'untimed',
      timeLimit: 140,
      question:
        'A and B can paint a fence together in 6 hours. A alone takes 10 hours. How long would B alone take?',
      options: ['15 hours', '12 hours', '18 hours', '20 hours', '16 hours'],
      correct: 0,
      explanation:
        'Rate A = §1/10§, Rate together = §1/6§. Rate B = §1/6 - 1/10§. Common denominator 30: §5/30 - 3/30 = 2/30 = 1/15§. B takes §15§ hours.',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'If 5 workers can complete a job in 12 days, how long will it take 8 workers to complete the same job (assuming same rate per worker)?',
      options: ['6 days', '7.5 days', '8 days', '9 days', '10 days'],
      correct: 1,
      explanation:
        'Total work = §5 × 12 = 60§ worker-days. With 8 workers: §60/8 = 7.5§ days.',
    },
    {
      type: 'untimed',
      timeLimit: 140,
      question:
        'Anna can type a document in 4 hours. Ben can type the same document in 6 hours. They work together for 1 hour, then Anna finishes alone. What is the total time?',
      options: ['2.7 hours', '2.8 hours', '3 hours', '3.2 hours', '3.3 hours'],
      correct: 4,
      explanation:
        "Anna's rate = §1/4§, Ben's rate = §1/6§. Combined rate = §1/4 + 1/6 = 3/12 + 2/12 = 5/12§. Work done together in 1 hour = §5/12§. Remaining work = §1 - 5/12 = 7/12§. Time for Anna alone = §(7/12) ÷ (1/4) = (7/12) × 4 = 7/3 ≈ 2.33§ hours. Total time = §1 + 2.33 = 3.33§ hours.",
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'A factory has §3§ machines. Machine X produces §20§ units/hour, Machine Y produces 30 units/hour, and Machine Z produces 40 units/hour. How many hours to produce 540 units with all machines?',
      options: ['4 hours', '5 hours', '6 hours', '7 hours', '8 hours'],
      correct: 2,
      explanation:
        'Combined rate = §20 + 30 + 40 = 90§ units/hour. Time = §540/90 = 6§ hours.',
    },
    {
      type: 'untimed',
      timeLimit: 130,
      question:
        'Tom can paint a house in 10 days. Jerry can paint it in 15 days. Tom works alone for 4 days, then they work together. How many more days to finish?',
      options: ['2 days', '3 days', '3.6 days', '4 days', '4.8 days'],
      correct: 2,
      explanation:
        'Tom alone for 4 days: §4 × 1/10 = 4/10 = 2/5§ done. Remaining = §3/5§. Combined rate = §1/10 + 1/15 = 3/30 + 2/30 = 5/30 = 1/6§. Time together = §(3/5) / (1/6) = 3/5 × 6 = 18/5 = 3.6§ days.',
    },
    {
      type: 'untimed',
      timeLimit: 140,
      question:
        'A swimming pool can be filled by Pipe A in 8 hours and by Pipe B in 12 hours. A drain can empty it in 24 hours. With all three open, how long to fill the pool?',
      options: ['4 hours', '4.8 hours', '5.5 hours', '6 hours', '6.5 hours'],
      correct: 3,
      explanation:
        'Rate A = §1/8§, Rate B = §1/12§, Drain = §-1/24§. Net rate = §1/8 + 1/12 - 1/24§. Convert to common denominator 24: §3/24 + 2/24 - 1/24 = 4/24 = 1/6§. Time = §1 ÷ (1/6) = 6§ hours.',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question:
        'If 8 workers can complete a task in 12 days, how many workers are needed to complete it in 6 days?',
      options: ['16', '12', '20', '14', '10'],
      correct: 0,
      explanation:
        'Total work = §8 × 12 = 96§ worker-days. For 6 days: Workers = §96/6 = 16§.',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'A printer can print 600 pages in 10 minutes. At this rate, how long to print 1500 pages?',
      options: [
        '25 minutes',
        '20 minutes',
        '30 minutes',
        '15 minutes',
        '35 minutes',
      ],
      correct: 0,
      explanation:
        'Rate = §600/10 = 60§ pages/minute. Time = §1500/60 = 25§ minutes.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'John can mow a lawn in 40 minutes, and Sarah can mow the same lawn in 60 minutes. If they work together for 15 minutes, what fraction of the lawn is mowed?',
      options: ['5/8', '1/2', '3/4', '2/3', '7/12'],
      correct: 0,
      explanation:
        'Rate John = §1/40§, Rate Sarah = §1/60§. Combined = §1/40 + 1/60 = 3/120 + 2/120 = 5/120 = 1/24§. In 15 min: §15 × (1/24) = 15/24 = 5/8§.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question:
        'Machine A completes a job in 6 hours; Machine B completes it in 9 hours. Compare the two quantities.',
      quantityA: 'Machine A rate',
      quantityB: 'Machine B rate',
      correct: 0,
      explanation:
        'Compare the two quantities directly. Quantity A is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'Which expressions represent work rates? Select all that apply.',
      options: [
        '§1/5§ job per hour',
        '§3§ jobs in 6 hours',
        '§10§ miles per hour',
        '§1/t§ job per hour for a job done in §t§ hours',
        '§$20§ per job',
      ],
      correct: [0, 1, 3],
      explanation:
        'Work rate measures jobs per unit time. Choices A, B, and D are work rates.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question:
        'If A does a job in 4 hours and B does it in 6 hours, how many hours do they need together?',
      correctAnswer: '12/5',
      acceptedAnswers: ['12/5'],
      explanation:
        'Combined rate is §1/4 + 1/6 = 5/12§ job/hour, so time is §12/5§ hours.',
    },
  ];

  const quickReference = [
    'Combined rate = Rate₁ + Rate₂',
    '§Time = Work / Rate§',
    '§Rate = 1 / Time§ (for one job)',
    '§Work = Rate \\times Time§',
    '§Efficiency = Actual / Standard§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Work/Rate problems?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Work and Rate problems for the GRE Quantitative section',
    aicontext:
      'Master individual work, combined rates, efficiency, and complex work scenarios',
  };

  return (
    <LessonTemplate
      lessonId="work-rate"
      lessonTitle="Work Rate"
      lessonIcon={<FiTool />}
      lessonDescription="Master individual work, combined rates, efficiency, and complex work scenarios"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="work-rate" />
      }
    />
  );
};

export default WorkRateLesson;
